import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { 
  analyzeMermaidSyntax, 
  attemptToFixMermaidCode,
  detectDiagramType,
  checkBrowserCompatibility
} from '@/utils/mermaidDebug';

// Função para acessar o mermaid global
function getMermaid() {
  if (typeof window === 'undefined' || !window.mermaid) {

    return null;
  }
  return window.mermaid;
}

// Componente para depuração de diagramas Mermaid
const MermaidDebugger = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [diagrams, setDiagrams] = useState<{ id: string; content: string; status: string }[]>([]);

  // Verificar todos os diagramas Mermaid na página
  const checkDiagrams = () => {
    const mermaidDivs = document.querySelectorAll('.mermaid');
    const diagramsData = Array.from(mermaidDivs).map((div, index) => {
      const id = div.id || `mermaid-${index}`;
      const content = div.textContent || '';
      // Verifica se tem um SVG dentro (foi renderizado)
      const isRendered = div.querySelector('svg') !== null;
      const status = isRendered ? 'Renderizado' : 'Não renderizado';
      
      return { id, content, status };
    });
    
    setDiagrams(diagramsData);
    setIsOpen(true);
  };

  // Tentar renderizar um diagrama específico
  const renderDiagram = (content: string, index: number) => {
    const mermaid = getMermaid();
    if (!mermaid) {

      return;
    }
    
      try {
      // Analisar o diagrama antes de renderizar
      const type = detectDiagramType(content);
      const syntaxAnalysis = analyzeMermaidSyntax(content);
      const compatibility = checkBrowserCompatibility();
      
      // Tentar corrigir o diagrama se necessário
      let diagramToRender = content;
      let fixApplied = false;
      
      if (syntaxAnalysis.length > 0 || syntaxAnalysis.some(issue => issue.includes('Issue') || issue.includes('Erro'))) {
        diagramToRender = attemptToFixMermaidCode(content);
        fixApplied = true;
      }
      
      // Exibir informações de diagnóstico
      const debugContainer = document.getElementById('debug-result');
      if (debugContainer) {
        debugContainer.innerHTML = `
          <div class="mb-3">
            <strong>Tipo:</strong> ${type || 'Desconhecido'}<br>
            <strong>Compatibilidade:</strong> ${Array.isArray(compatibility) ? compatibility.join('<br>') : compatibility}<br>
            <strong>Análise:</strong> ${Array.isArray(syntaxAnalysis) ? syntaxAnalysis.join('<br>') : syntaxAnalysis}<br>
            ${fixApplied ? '<strong>Correção aplicada!</strong>' : ''}
          </div>
        `;
      }
      
      // Renderizar o diagrama (original ou corrigido)
      const tempId = `debug-diagram-${Date.now()}-${index}`;
      mermaid.render(tempId, diagramToRender).then((result: any) => {
        // Exibe o SVG gerado
        if (debugContainer) {
          // Preservar as informações de diagnóstico e adicionar o SVG
          const currentContent = debugContainer.innerHTML;
          debugContainer.innerHTML = currentContent + result.svg;
        }        // Atualiza o status do diagrama
        const updatedDiagrams = [...diagrams];
        updatedDiagrams[index].status = fixApplied ? 'Corrigido e renderizado' : 'Renderizado manualmente';
        setDiagrams(updatedDiagrams);
        
      }).catch((error: any) => {

        if (debugContainer) {
          const currentContent = debugContainer.innerHTML;
          debugContainer.innerHTML = currentContent + `
            <div class="p-3 mt-2 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded">
              <strong>Erro de renderização:</strong> ${error.message || 'Erro desconhecido'}
            </div>
          `;
        }
      });
    } catch (e: any) {

      const debugContainer = document.getElementById('debug-result');
      if (debugContainer) {
        debugContainer.innerHTML = `
          <div class="p-3 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded">
            <strong>Erro ao processar diagrama:</strong> ${e.message || 'Erro desconhecido'}
          </div>
        `;
      }
    }
  };

  // Forçar a re-renderização de todos os diagramas
  const reprocessAllDiagrams = () => {
    const mermaid = getMermaid();
    if (!mermaid) {

      return;
    }
    
    try {
      mermaid.initialize({
        startOnLoad: false,
        theme: document.documentElement.classList.contains('dark') ? 'dark' : 'default',
        securityLevel: 'loose',
        logLevel: 'debug' // Temporariamente aumenta o nível de log para depuração
      });
      
      // Abordagem 1: Usar o método run do Mermaid
      mermaid.run({
        querySelector: '.mermaid',
        suppressErrors: false
      }).then(() => {
        checkDiagrams(); // Atualiza a lista de diagramas
      }).catch((error: any) => {

        
        // Abordagem 2: Renderizar cada diagrama individualmente
        document.querySelectorAll('.mermaid').forEach((element, index) => {
          const content = element.textContent || '';
          if (content && !element.querySelector('svg')) {
            const tempId = `reprocess-${Date.now()}-${index}`;
            try {
              mermaid.render(tempId, content).then((result: any) => {
                element.innerHTML = result.svg;
              }).catch((innerError: any) => {

              });
            } catch (individualError) {

            }
          }
        });
        
        // Atualiza a lista após um curto atraso
        setTimeout(checkDiagrams, 500);
      });
    } catch (e) {

    }
  };

  // Forçar a reinicialização da biblioteca Mermaid com configurações específicas
  const reinitializeMermaid = () => {
    const mermaid = getMermaid();
    if (!mermaid) {

      return;
    }
    
    try {
      // Configuração especial para focar nos diagramas problemáticos
      mermaid.initialize({
        startOnLoad: false,
        theme: document.documentElement.classList.contains('dark') ? 'dark' : 'default',
        securityLevel: 'loose',
        logLevel: 'debug',
        flowchart: {
          useMaxWidth: false,
          htmlLabels: true
        },
        sequence: {
          actorMargin: 80,
          messageMargin: 40
        },
        gantt: {
          barHeight: 40
        }
      });
      
      // Atualiza as informações
      const debugContainer = document.getElementById('debug-result');
      if (debugContainer) {
        debugContainer.innerHTML = `
          <div class="p-3 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded">
            <strong>Mermaid reinicializado!</strong><br>
            Configurado com suporte explícito para:<br>
            - graph TD (flowchart)<br>
            - flowchart TD<br>
            - sequenceDiagram
          </div>
        `;
      }
      
      // Reprocessa todos os diagramas
      reprocessAllDiagrams();
    } catch (e) {

    }
  };

  return (
    <>
      <div className="fixed bottom-4 right-4 z-50">
        <Button variant="outline" size="sm" onClick={checkDiagrams}>
          Diagnosticar Diagramas
        </Button>
      </div>
      
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-3xl h-[80vh]">
          <DialogHeader>
            <DialogTitle>Diagnóstico de Diagramas Mermaid</DialogTitle>
            <DialogDescription>
              Encontrados {diagrams.length} diagramas na página
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex space-x-2 mb-4">
            <Button variant="outline" size="sm" onClick={reprocessAllDiagrams}>
              Reprocessar Todos
            </Button>
            <Button variant="outline" size="sm" onClick={reinitializeMermaid}>
              Reinicializar Mermaid
            </Button>
          </div>
          
          <ScrollArea className="h-[50vh]">
            {diagrams.map((diagram, index) => (
              <div key={index} className="border p-4 mb-4 rounded-md">
                <div className="flex justify-between items-center mb-2">
                  <div>
                    <span className="font-medium">{diagram.id}</span>
                    <span className={`ml-2 text-xs px-2 py-1 rounded ${
                      diagram.status.includes('Renderizado') 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}>
                      {diagram.status}
                    </span>
                  </div>
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => renderDiagram(diagram.content, index)}
                  >
                    Renderizar
                  </Button>
                </div>
                
                <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded text-xs font-mono overflow-x-auto">
                  {diagram.content.substring(0, 100)}
                  {diagram.content.length > 100 ? '...' : ''}
                </div>
              </div>
            ))}
          </ScrollArea>
          
          <div className="mt-4">
            <h4 className="font-medium mb-2">Resultado de Depuração</h4>
            <div 
              id="debug-result"
              className="border rounded-md p-4 h-[20vh] overflow-auto bg-slate-50 dark:bg-slate-900"
            >
              Selecione um diagrama para renderizar
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setIsOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MermaidDebugger;
